import { AlertCircle, CreditCard, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  formatCompactBytes,
  formatPlanPrice,
  mostPressingBar,
  planThatLifts,
  usageBars,
} from '../utils/planDisplay'
import type { UsageBar } from '../utils/planDisplay'
import type {
  PlanDocument,
  SubscriptionDocument,
  UsageDocument,
} from '@/lib/types/subscription'
import { SUBSCRIPTION_STATUS, USAGE_LIMITS } from '@/lib/types/subscription'
import { Button } from '@/components/ui/button'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { cn } from '@/lib/utils'

type Props = {
  plan: PlanDocument
  subscription: SubscriptionDocument
  usage: UsageDocument | null
  /** Public plan catalogue, to name the plan that lifts a limit. */
  plans: Array<PlanDocument>
  isTrialing: boolean
  trialDaysLeft: number
  isActive: boolean
  isLoading: boolean
  isApiDown: boolean
  offlineTitle?: string
  onManageBilling: () => void
  onCancel: () => void
  onReactivate: () => void
}

/**
 * Current plan with a "Current" badge and usage bars (design 15c). The
 * billing actions (portal, cancel, reactivate) are the same handlers the page
 * always had; only their placement changed.
 */
export function CurrentPlanCard({
  plan,
  subscription,
  usage,
  plans,
  isTrialing,
  trialDaysLeft,
  isActive,
  isLoading,
  isApiDown,
  offlineTitle,
  onManageBilling,
  onCancel,
  onReactivate,
}: Props) {
  const { t } = useTranslation('settings')
  const bars = usageBars(plan, usage)
  const pressing = mostPressingBar(bars)
  const lifter = pressing ? planThatLifts(plans, pressing.key, pressing.limit) : null
  const isPaid = plan.priceMonthly > 0
  const fmt = useDateTimeFormatters()
  const periodEnd = fmt.date(new Date(subscription.currentPeriodEnd))

  const statusBadge =
    subscription.status !== SUBSCRIPTION_STATUS.ACTIVE && !isTrialing
      ? t(`subscription.status.${subscription.status}`, { defaultValue: subscription.status })
      : null

  const formatValue = (bar: UsageBar, value: number) =>
    bar.key === USAGE_LIMITS.STORAGE ? formatCompactBytes(value) : String(value)

  const warning = pressing
    ? [
        t(`subscription.usage.${pressing.key}.${pressing.over ? 'over' : 'near'}`, {
          limit: formatValue(pressing, pressing.limit),
        }),
        lifter
          ? t(lifter.removes ? 'subscription.usage.removes' : 'subscription.usage.raises', {
              plan: lifter.plan.name,
            })
          : '',
      ]
        .filter(Boolean)
        .join(' ')
    : null

  return (
    <div className="rounded-[14px] border bg-card p-[18px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold">{plan.name}</h2>
            <span className="rounded-full bg-foreground px-2 py-0.5 text-[11px] font-semibold text-background">
              {t('subscription.badge.current')}
            </span>
            {statusBadge && (
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
                {statusBadge}
              </span>
            )}
            {isTrialing && (
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-semibold text-brand-tint-foreground">
                <Sparkles className="size-3" aria-hidden />
                {t('subscription.badge.daysLeft', { days: trialDaysLeft })}
              </span>
            )}
            {subscription.cancelAtPeriodEnd && (
              <span className="rounded-full border border-amber-400/50 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                {t('subscription.badge.cancels', { date: periodEnd })}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">{plan.description}</p>
        </div>

        {subscription.status === SUBSCRIPTION_STATUS.ACTIVE && isPaid && (
          <p className="text-right text-sm">
            <span className="font-semibold">
              {formatPlanPrice(
                subscription.billingInterval === 'monthly' ? plan.priceMonthly : plan.priceAnnual,
                plan.currency,
              )}
            </span>{' '}
            <span className="text-xs text-muted-foreground">
              {subscription.billingInterval === 'monthly'
                ? t('subscription.perMonth')
                : t('subscription.perYear')}
            </span>
          </p>
        )}
      </div>

      {subscription.cancelAtPeriodEnd && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          {t('subscription.cancelNotice', { date: periodEnd })}
        </p>
      )}

      {/* Usage vs limits */}
      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        {bars.map((bar) => (
          <div key={bar.key} className="min-w-0">
            <div className="flex items-baseline justify-between gap-2 text-[13px]">
              <dt className="text-muted-foreground">{t(`subscription.usage.${bar.key}.label`)}</dt>
              <dd className="truncate tabular-nums">
                <span className="font-semibold text-foreground">{formatValue(bar, bar.used)}</span>
                <span className="text-muted-foreground">
                  {' / '}
                  {bar.unlimited ? t('subscription.usage.unlimited') : formatValue(bar, bar.limit)}
                </span>
              </dd>
            </div>
            <div
              className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary"
              role="progressbar"
              aria-label={t(`subscription.usage.${bar.key}.label`)}
              aria-valuemin={0}
              aria-valuemax={bar.unlimited ? undefined : bar.limit}
              aria-valuenow={bar.used}
            >
              <div
                className={cn(
                  'h-full rounded-full transition-[width]',
                  bar.near || bar.over ? 'bg-brand' : 'bg-foreground',
                )}
                style={{ width: `${bar.unlimited ? 0 : Math.max(bar.percent, bar.used > 0 ? 2 : 0)}%` }}
              />
            </div>
          </div>
        ))}
      </dl>

      {warning && (
        <p className="mt-3 flex items-center gap-1.5 text-[13px] text-brand" role="status">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden />
          {warning}
        </p>
      )}

      {/* Billing actions — only for paid, active plans (Free has none) */}
      {isActive && isPaid && (
        <div className="mt-4 flex flex-wrap gap-2 border-t pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onManageBilling}
            disabled={isLoading || isApiDown}
            title={offlineTitle}
          >
            <CreditCard className="size-4" />
            {t('subscription.actions.manageBilling')}
          </Button>
          {subscription.cancelAtPeriodEnd ? (
            <Button
              size="sm"
              onClick={onReactivate}
              disabled={isLoading || isApiDown}
              title={offlineTitle}
            >
              {isLoading ? t('subscription.actions.reactivating') : t('subscription.actions.reactivate')}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              disabled={isLoading || isApiDown}
              title={offlineTitle}
              className={cn('text-muted-foreground')}
            >
              {isLoading ? t('subscription.actions.canceling') : t('subscription.actions.cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
